// src/Pages/Auth/Forgot.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { toast } from "react-toastify";
import { api } from "../../services/api.js";
import logoDark from "../../assets/logo-buukkeri-dark.webp"; // <-- LISÄTTY

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

export default function Forgot() {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const error =
    touched && (!email.trim() ? "Sähköposti vaaditaan." :
    !emailRegex.test(email.trim()) ? "Sähköposti ei ole kelvollinen." : "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (error) return;

    setLoading(true);
    try {
      // Backend palauttaa aina ok:true (ei paljasta löytyikö osoite) → hyvä.
      await api.auth.passwordForgot(email.trim());
      setSent(true);
      toast.success("Mikäli osoite on meillä, palautusohjeet ovat matkalla. Tarkista myös roskapostisi.", { containerId: "global" });
    } catch {
      // Sama viesti myös virheessä (turvallisuus).
      setSent(true);
      toast.success("Mikäli osoite on meillä, palautusohjeet ovat matkalla. Tarkista myös roskapostisi.", { containerId: "global" });
    } finally {
      setLoading(false);
    }
  };

  const labelBase =
    "block font-customFont1 text-sm uppercase tracking-wide text-black/70 dark:text-white/80 mb-1";
  const inputBase =
    "w-full h-11 rounded-lg px-3 font-customFont1 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-pink/60 dark:focus:ring-primary text-base";

  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40">
      <div
        className={[
          "mx-auto max-w-6xl px-4 pb-12",
          "pt-24",
          "md:min-h-[calc(100vh-96px)] md:grid md:place-items-center",
        ].join(" ")}
      >
        <div className="w-full md:max-w-4xl lg:max-w-5xl mx-auto">

          {/* Logo – sama malli kuin login/register */}
          <div className="mb-4 md:mb-6 flex justify-center">
            <Link to="/" aria-label="Siirry etusivulle" className="inline-flex items-center">
              <img
                src={logoDark}
                alt="Buukkeri"
                className="h-24 w-auto md:h-28"
                loading="lazy"
                decoding="async"
              />
            </Link>
          </div>

          {/* Otsikko keskelle myös mobiilissa */}
          <h1 className="text-2xl font-bold text-center text-black/80 dark:text-white/90 mb-3">
            Unohtuiko salasana?
          </h1>

          <div
            className={[
              "grid grid-cols-1 gap-6",
              "md:grid-cols-2 md:items-start md:justify-center",
            ].join(" ")}
          >
            {/* Lomake */}
            <div className="order-2 md:order-2">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/60 backdrop-blur-md shadow-lg w-full p-4 sm:p-5">
                {!sent ? (
                  <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div>
                      <label className={labelBase} htmlFor="email">Sähköposti</label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => setTouched(true)}
                        className={inputBase}
                        autoComplete="email"
                        required
                        aria-invalid={!!error}
                        disabled={loading}
                      />
                      {error && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
                      )}
                    </div>

                    <NeonButton
                      type="submit"
                      variant="primary"
                      size="md"
                      className="w-full flex items-center justify-center gap-2 disabled:opacity-60"
                      disabled={loading || !!error}
                      aria-busy={loading ? "true" : "false"}
                    >
                      {loading ? "Lähetetään…" : "Lähetä palautuslinkki"}
                    </NeonButton>

                    <p className="text-xs text-black/60 dark:text-white/60">
                      Lähetämme sähköpostiisi linkin, jolla voit asettaa uuden salasanan.
                    </p>
                  </form>
                ) : (
                  <div className="space-y-3">
                    <p className="text-black/80 dark:text-white/80">
                      Jos osoite <span className="font-semibold">{email}</span> löytyy, lähetimme
                      palautuslinkin. Tarkista myös roskaposti ja mainokset.
                    </p>
                    <p className="text-sm text-black/60 dark:text-white/60">
                      Linkki on voimassa rajatun ajan. Klikkaus avaa sivun, jossa voit syöttää uuden salasanan.
                    </p>
                    <div className="flex gap-2">
                      <NeonButton as={Link} to="/kirjaudu" variant="ghost" size="sm">
                        Takaisin kirjautumiseen
                      </NeonButton>
                      <NeonButton as={Link} to="/rekisteroidy" variant="ghost" size="sm">
                        Luo uusi tili
                      </NeonButton>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info/CTA */}
            <div className="order-1 md:order-1">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur p-4">
                <p className="text-black/80 dark:text-white/80 text-sm leading-relaxed text-center">
                  Syötä sähköpostiosoitteesi niin lähetämme palautuslinkin. Kun saat viestin, seuraa siinä
                  olevaa linkkiä ja aseta uusi salasana.
                </p>

                <div className="mt-4 flex justify-center">
                  <NeonButton as={Link} to="/" variant="ghost" size="sm">
                    Palaa etusivulle
                  </NeonButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
}
